import { createClient } from '@supabase/supabase-js';

// .trim() guards against stray whitespace in deploy-time env vars (a trailing
// space in a deployed VITE_SUPABASE_URL broke string-concatenated URLs in the
// other apps with ERR_NAME_NOT_RESOLVED).
const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || '').trim();
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY || '').trim();

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. ' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Get a valid access token, refreshing if needed.
 * Returns null if no valid session exists.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session?.access_token) {
    return null;
  }

  // Check if token expires within 5 minutes
  if (session.expires_at) {
    const expiresAt = session.expires_at * 1000; // Convert to ms
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;

    if (expiresAt - now < fiveMinutes) {
      console.log('[Supabase] Token expiring soon, refreshing session...');
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError) {
        console.error('[Supabase] Failed to refresh session:', refreshError);
        return null;
      }
      return refreshData.session?.access_token ?? null;
    }
  }

  return session.access_token;
}

/**
 * Handle an invalid session by attempting refresh, or signing out if that fails.
 * Returns true if session was successfully refreshed, false if signed out.
 */
export async function handleInvalidSession(): Promise<boolean> {
  console.log('[Supabase] Handling invalid session...');
  const { error } = await supabase.auth.refreshSession();
  if (error) {
    console.log('[Supabase] Session invalid, signing out...');
    await supabase.auth.signOut();
    return false;
  }
  console.log('[Supabase] Session refreshed successfully');
  return true;
}

export type HowieConversation = {
  id: string;
  user_id: string;
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
    /** Ask answers that searched the web (see components/WebSources). */
    webSearches?: Array<{ query: string; resultsUsed?: number }>;
    citations?: Array<{ url: string; title?: string }>;
  }>;
  created_at: string;
  updated_at: string;
  last_message?: string;
};

export type HowieCorridorConversation = {
  id: string;
  user_id: string;
  module_id: string;
  current_phase: string;
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    timestamp?: string;
  }>;
  created_at: string;
  updated_at: string;
  completed_at?: string;
};

export type HowieCorridorProgress = {
  id: string;
  user_id: string;
  module_id: string;
  status: 'not_started' | 'in_progress' | 'completed';
  started_at?: string;
  completed_at?: string;
};

export type HowieCompanionSurface = 'generic' | 'meet' | 'gmail';

export type HowieCompanionSession = {
  id: string;
  user_id: string;
  surface: HowieCompanionSurface;
  title: string | null;
  url: string | null;
  status: 'active' | 'ended' | 'report_ready';
  meeting_code: string | null;
  gmail_subject: string | null;
  last_advice: string | null;
  report_status: 'none' | 'pending' | 'ready' | 'failed';
  started_at: string;
  ended_at: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
};

export type HowieCompanionSnippet = {
  id: string;
  session_id: string;
  user_id: string;
  label: string;
  text: string;
  source: 'caption' | 'gmail' | 'page' | 'selected' | 'artifact';
  created_at: string;
  metadata: Record<string, unknown>;
};

export type HowieCompanionAdviceCard = {
  id?: string;
  sessionId?: string;
  surface?: HowieCompanionSurface;
  title?: string;
  nudge?: string;
  suggestedWords?: string | null;
  why?: string | null;
  howLens?: string | null;
  triggerSnippetIds?: string[];
  createdAt?: string;
};

export type HowieCompanionMessage = {
  id: string;
  session_id: string;
  user_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  card: HowieCompanionAdviceCard | null;
  created_at: string;
  metadata: Record<string, unknown>;
};

export type HowieCompanionReport = {
  id: string;
  session_id: string;
  user_id: string;
  status: 'pending' | 'ready' | 'failed';
  title: string;
  body_markdown: string;
  scorecard: Record<string, unknown>;
  follow_up: Record<string, unknown>;
  email_sent_at: string | null;
  created_at: string;
  updated_at: string;
  metadata: Record<string, unknown>;
};
